import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Badge, Button, Alert } from 'react-bootstrap';
import { 
  IconEye, 
  IconEyeOff, 
  IconCurrencyReal, 
  IconPackage, 
  IconWorld, 
  IconPhoto, 
  IconMessageCircle, 
  IconFileText,
  IconX 
} from '@tabler/icons-react';
import { IFRAME_SECURITY } from '../../constants/formConstants';

// Helper function to safely get image source
const getImageSrc = (imageData) => {
  try {
    if (!imageData) return null;
    
    if (typeof imageData === 'string') {
      return imageData;
    }
    
    if (imageData instanceof File || imageData instanceof Blob) {
      return URL.createObjectURL(imageData);
    }
    
    // Handle object with url/preview properties
    if (typeof imageData === 'object') {
      return imageData.url || imageData.preview || imageData.src || null;
    }
    
    return null;
  } catch (error) {
    console.warn('Erro ao processar imagem:', error, imageData);
    return null;
  }
};

const Step7Review = ({ 
  formData, 
  categories = [], 
  isEditing = false 
}) => {
  const [showPlaygroundPreview, setShowPlaygroundPreview] = useState(false);
  
  // Encontrar categoria selecionada
  const selectedCategory = useMemo(() => {
    if (!Array.isArray(categories) || !formData.categories) return null;
    return categories.find(cat => cat.id === formData.categories);
  }, [categories, formData.categories]);

  // Calcular preço final com desconto
  const finalPrice = useMemo(() => {
    const basePrice = parseFloat(formData.price || 0);
    const discount = parseInt(formData.pricing_info?.discount_percentage || 0);
    return discount > 0 ? basePrice * (1 - discount / 100) : basePrice;
  }, [formData.price, formData.pricing_info?.discount_percentage]);

  // Formatador de moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handlePlaygroundPreviewToggle = () => {
    setShowPlaygroundPreview(!showPlaygroundPreview);
  };

  return (
    <div className="step-content">
      <Row className="justify-content-center">
        <Col lg={10}>
          {/* Header */}
          <div className="text-center mb-4">
            <h3 className="fw-bold mb-2">Revisar e Confirmar</h3>
            <p className="text-muted">
              Verifique todas as informações antes de{" "}
              {isEditing ? "atualizar" : "publicar"} seu agente
            </p>
          </div>

          <Row>
            {/* Coluna Esquerda */}
            <Col md={6} className="mb-4">
              {/* Agente & Valores */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0 d-flex align-items-center">
                    <IconCurrencyReal size={20} className="me-2" />
                    Agente & Valores
                  </h5>
                </Card.Header>
                <Card.Body>
                  {formData.agent_name && (
                    <p className="mb-2">
                      <strong>Nome do Agente:</strong> {formData.agent_name}
                    </p>
                  )}
                  {formData.agent_job && (
                    <p className="mb-2">
                      <strong>Função:</strong> {formData.agent_job}
                    </p>
                  )}
                  <p className="mb-2">
                    <strong>Valor Original:</strong> {formatCurrency(parseFloat(formData.price || 0))}
                  </p>
                  {formData.pricing_info?.discount_percentage && (
                    <p className="mb-2">
                      <strong>Desconto:</strong> {formData.pricing_info.discount_percentage}%
                    </p>
                  )}
                  <p className="mb-2">
                    <strong>Valor Final:</strong> 
                    <span className="text-success fw-bold"> {formatCurrency(finalPrice)}</span>
                  </p>
                  {formData.pricing_info?.trial_period_days && (
                    <p className="mb-0">
                      <strong>Período de Trial:</strong> {formData.pricing_info.trial_period_days} dias
                    </p>
                  )}
                </Card.Body>
              </Card>

              {/* Categoria */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0 d-flex align-items-center">
                    <IconPackage size={20} className="me-2" />
                    Categoria
                  </h5>
                </Card.Header>
                <Card.Body>
                  {formData.agent_description && (
                    <p className="mb-2">
                      <strong>Descrição:</strong> {formData.agent_description}
                    </p>
                  )}
                  <div>
                    <strong>Categoria:</strong>
                    {selectedCategory ? (
                      <Badge bg="primary" className="ms-2">
                        {selectedCategory.name}
                      </Badge>
                    ) : (
                      <span className="text-muted ms-2">Nenhuma categoria selecionada</span>
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Plataforma */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0 d-flex align-items-center">
                    <IconWorld size={20} className="me-2" />
                    Plataforma
                  </h5>
                </Card.Header>
                <Card.Body>
                  {formData.platform_name && (
                    <p className="mb-2">
                      <strong>Nome:</strong> {formData.platform_name}
                    </p>
                  )}
                  {formData.platform_url && (
                    <p className="mb-2">
                      <strong>URL:</strong> {formData.platform_url}
                    </p>
                  )}
                  {formData.contact_name && (
                    <p className="mb-0">
                      <strong>Suporte:</strong> {formData.contact_name}
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Coluna Direita */}
            <Col md={6} className="mb-4">
              {/* Mídia */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0 d-flex align-items-center">
                    <IconPhoto size={20} className="me-2" />
                    Mídia
                  </h5>
                </Card.Header>
                <Card.Body>
                  {formData.banner && (
                    <div className="mb-3">
                      <p className="fw-bold mb-2">Banner:</p>
                      {(() => {
                        const bannerSrc = getImageSrc(formData.banner);
                        return bannerSrc ? (
                          <img
                            src={bannerSrc}
                            alt="Banner"
                            className="img-fluid rounded"
                            style={{ maxHeight: '100px', width: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              console.warn('Erro ao carregar imagem do banner:', formData.banner);
                            }}
                          />
                        ) : (
                          <div className="bg-light rounded p-3 text-center text-muted">
                            <IconPhoto size={24} />
                            <p className="mb-0 small mt-1">Banner não disponível</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {formData.video_url && (
                    <p className="mb-2">
                      <strong>Vídeo:</strong> 
                      <a href={formData.video_url} target="_blank" rel="noopener noreferrer" className="ms-2">
                        {formData.video_url}
                      </a>
                    </p>
                  )}
                  {formData.gallery_images?.length > 0 && (
                    <div>
                      <p className="fw-bold mb-2">
                        Galeria ({formData.gallery_images.length} {formData.gallery_images.length === 1 ? 'imagem' : 'imagens'}):
                      </p>
                      <Row className="g-2">
                        {formData.gallery_images.slice(0, 8).map((image, index) => {
                          const imageSrc = getImageSrc(image);
                          return (
                            <Col xs={3} key={index}>
                              {imageSrc ? (
                                <img
                                  src={imageSrc}
                                  alt={`Gallery ${index + 1}`}
                                  className="img-fluid rounded"
                                  style={{ height: '50px', width: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    console.warn('Erro ao carregar imagem da galeria:', image);
                                  }}
                                />
                              ) : (
                                <div 
                                  className="bg-light rounded d-flex align-items-center justify-content-center"
                                  style={{ height: '50px', width: '100%' }}
                                >
                                  <IconPhoto size={16} className="text-muted" />
                                </div>
                              )}
                            </Col>
                          );
                        })}
                      </Row>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Playground */}
              {formData.playground_url && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0 d-flex align-items-center">
                      <IconMessageCircle size={20} className="me-2" />
                      Playground
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-3">
                      <strong>URL do Chat:</strong> 
                      <a href={formData.playground_url} target="_blank" rel="noopener noreferrer" className="ms-2">
                        {formData.playground_url}
                      </a>
                    </p>

                    {/* Botão de Preview */}
                    <div className="text-center mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handlePlaygroundPreviewToggle}
                      >
                        {showPlaygroundPreview ? (
                          <>
                            <IconEyeOff size={16} className="me-1" />
                            Ocultar Preview
                          </>
                        ) : (
                          <>
                            <IconEye size={16} className="me-1" />
                            Visualizar Preview
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Preview do Playground */}
                    {showPlaygroundPreview && (
                      <div className="border rounded">
                        <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
                          <small className="fw-bold">Preview do Playground</small>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-secondary"
                            onClick={handlePlaygroundPreviewToggle}
                          >
                            <IconX size={16} />
                          </Button>
                        </div>
                        <div style={{ height: IFRAME_SECURITY.REVIEW_HEIGHT, overflow: 'hidden' }}>
                          <iframe
                            src={formData.playground_url}
                            style={{ 
                              width: '100%', 
                              height: '100%',
                              border: 'none'
                            }}
                            title="Playground Preview"
                            sandbox={IFRAME_SECURITY.SANDBOX}
                          />
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Conteúdo */}
              <Card>
                <Card.Header>
                  <h5 className="mb-0 d-flex align-items-center">
                    <IconFileText size={20} className="me-2" />
                    Conteúdo
                  </h5>
                </Card.Header>
                <Card.Body>
                  {formData.description && (
                    <div className="mb-3">
                      <p className="fw-bold mb-2">Descrição:</p>
                      <div 
                        className="text-muted small"
                        style={{ maxHeight: '100px', overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: formData.description }}
                      />
                    </div>
                  )}

                  {formData.technical_specs && (
                    <div className="mb-3">
                      <p className="fw-bold mb-2">Especificações Técnicas:</p>
                      <div 
                        className="text-muted small"
                        style={{ maxHeight: '100px', overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: formData.technical_specs }}
                      />
                    </div>
                  )}

                  {formData.capabilities?.length > 0 && (
                    <div>
                      <p className="fw-bold mb-2">Capacidades:</p>
                      <div className="d-flex flex-wrap gap-1">
                        {formData.capabilities.slice(0, 10).map((capability, index) => (
                          <Badge key={index} bg="info" className="small">
                            {capability}
                          </Badge>
                        ))}
                        {formData.capabilities.length > 10 && (
                          <Badge bg="secondary" className="small">
                            +{formData.capabilities.length - 10} mais
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Alerta Final */}
          <Alert variant="warning" className="text-center">
            <strong>Atenção:</strong> Revise cuidadosamente todas as informações antes de prosseguir. 
            Após a {isEditing ? 'atualização' : 'publicação'}, algumas informações poderão precisar de aprovação para serem alteradas.
          </Alert>
        </Col>
      </Row>
    </div>
  );
};

export default Step7Review;